import { HttpClient, robotoHeaders } from "@/shared/http";
import type { JointBinding } from "@/shared/state/visualization/schema/v1/three-d";

import {
  adaptBindableMessagePaths,
  type UrdfBindableMessagePath,
} from "./bindableFields";
import { type VisualizationAssetSettings } from "./visualizationAsset";

interface Options {
  abortSignal: AbortSignal;
  resourceOwnerId: string;
}

/**
 * What the platform returns when asked to build a visualization asset.
 *
 * The conversion itself runs on a worker, so the response carries no artifact —
 * only the canonical path the artifact will land at. Progress is read back off
 * the source file's `roboto/visualization_asset` metadata.
 */
export interface VisualizationAssetRequestResponse {
  relative_path: string;
}

/**
 * What the platform returns when a URDF's default joint bindings are saved.
 *
 * `relative_path` names the generated glTF the declarations are being written
 * into — the asset's `ROBOTO_device` extension is the system of record, and
 * there is no metadata block anywhere holding a copy. The rewrite is queued onto
 * a worker rather than performed here, so the artifact at this path is stale for
 * a few seconds afterwards. That is by design; do not add a second polling
 * channel to chase it.
 */
export interface SetJointBindingsResponse {
  relative_path: string;
}

/**
 * The `/v1/urdf` API surface: everything that turns a robot description into a
 * viewable, articulated glTF and everything that edits one.
 *
 * Separate from {@link FileService} even though every route is addressed by a
 * file, because the routes are owned by the URDF router rather than the files
 * one, and a file mock has no business stubbing them.
 */
export class UrdfService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * The message paths of a single topic that a joint binding may name, with the
   * type and unit the topic's schema versions agree on.
   *
   * Resolves the topic the same way as `GET /v1/topics/unique/topic_fields`, but
   * returns one entry per distinct message path rather than the path alone. That
   * is what lets the binding dialog tell a scalar path from an array one without
   * reading a message.
   *
   * `topic_name` is a query parameter rather than a path segment because topic
   * names contain slashes (`/imu/data`).
   *
   * The response is read through {@link adaptBindableMessagePaths}, so a shape
   * this build does not understand degrades to fewer suggestions rather than to
   * a thrown error. Returns `[]` for a topic with no normalized schema rows yet.
   */
  public async getBindableMessagePaths(
    topicName: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<UrdfBindableMessagePath[]> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "getBindableMessagePaths requires an org ID, none was provided",
      );
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/urdf/bindable_topic_message_paths",
      new URLSearchParams({ topic_name: topicName }),
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    return adaptBindableMessagePaths(await response.json<unknown>());
  }

  /**
   * Asks the platform to convert a robot description into a viewable 3D asset.
   *
   * Returns as soon as the work is queued — the conversion runs on a worker,
   * which can take longer than an API request may. The response is only the
   * canonical dataset-relative path the `.gltf` will occupy; poll the source
   * file's `roboto/visualization_asset` metadata for progress.
   *
   * @param fileId the source `.urdf` file
   * @param settings converter options; see {@link VisualizationAssetSettings}
   * @returns the canonical relative path of the artifact being built
   */
  public async requestVisualizationAsset(
    fileId: string,
    settings: VisualizationAssetSettings,
    options?: Partial<Options>,
  ): Promise<VisualizationAssetRequestResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/urdf/files/${fileId}/visualization_asset`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      body: JSON.stringify(settings),
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<VisualizationAssetRequestResponse>();
  }

  /**
   * Records which topic and message path drives each joint of a robot
   * description, by queueing a rewrite of its generated glTF.
   *
   * *Addressed* by the **source `.urdf`**, never by the artifact: the route
   * derives the artifact's path from the source record, so no caller has to know
   * it. The declarations are *stored* in that artifact's `ROBOTO_device`
   * extension and nowhere else — which is also why the route 404s when no asset
   * has been generated yet, since there would be nothing to write to.
   *
   * `bindings` is the complete set, so a name omitted from it has its binding
   * removed and an empty record clears every one.
   *
   * @param fileId the source `.urdf` file
   * @param bindings one entry per joint, keyed by the joint's name in the URDF
   * @returns the canonical relative path of the artifact being patched
   */
  public async setJointBindings(
    fileId: string,
    bindings: Record<string, JointBinding>,
    options?: Partial<Options>,
  ): Promise<SetJointBindingsResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/urdf/files/${fileId}/visualization_asset/joint_bindings`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      body: JSON.stringify({ bindings }),
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<SetJointBindingsResponse>();
  }
}
